import React, { useCallback, useEffect, useMemo, useRef, useState, memo } from "react";
import { Button, Row } from "react-bootstrap";
import "../RegisteredUser/SupportPage.css";
import { useAuth } from "../../AuthContext";
import instance, { apiEncryptRequest, decryptData } from "../../Api";
import Loader from "../common/Loader";
import { FaX } from "react-icons/fa6";
import toast, { Toaster } from "react-hot-toast";
import AppToast from "../../AppToast";
import { useLocation } from "react-router-dom";

// ─── Pure helpers ─────────────────────────────────────────────────────────────
const isMobileDevice = () => window.matchMedia("(max-width: 800px)").matches;

const statusColor = (status) =>
  status === "completed" ? "green"
  : status === "progress" ? "#aaaa00"
  : status === "open"    ? "#f47c20"
  : "#aa0000";

const convertToString = (value) =>
  Array.isArray(value) ? value.join(", ") : value;

const MAX_FILE_SIZE = 2 * 1024 * 1024;

// ─── Ticket status select — same 6 options every time ────────────────────────
const StatusSelect = memo(({ item, onChange, fontSize = 18 }) => (
  <select
    name="plan_time"
    style={{ borderRadius: 4, background: statusColor(item.status), border: "none", fontSize, padding: "5px 15px", color: "#fff", fontWeight: 600, marginBottom: 10, width: fontSize < 18 ? 105 : undefined }}
    value={item.status}
    onChange={(e) => onChange({ id: item.id, status: e.target.value })}
  >
    <option value="completed" disabled>Complete</option>
    <option value="progress"  disabled>inProgress</option>
    <option value="open"      disabled>Open</option>
    <option value="pending"   disabled>Pending</option>
    <option value="archived"  disabled={item.archived === "archived"}>Archive</option>
    <option value="close">Close</option>
  </select>
));

// ─── Reply thread + reply input ───────────────────────────────────────────────
const ReplyThread = memo(({ replies, isMobile, canReply, fileInputRef, selectedImageRply, onAttach, replyText, setReplyText, onReply }) => (
  <div className="you-title" style={{ fontSize: isMobile ? undefined : 24, marginBottom: 20 }}>
    {replies.map((msg, idx) => (
      <div key={idx} style={{ maxHeight: "none" }}>
        <p style={{ marginTop: 10, marginRight: isMobile ? 0 : 200 }}>
          {msg.reply !== null && (
            isMobile ? (
              <span style={{ color: "#f47c20", fontWeight: 600 }}>
                You: <span style={{ whiteSpace: "pre-wrap" }}>{msg.reply}</span>
              </span>
            ) : (
              <div className="row" style={{ color: "#f47c20", fontWeight: 600, width: "126%", marginBottom: 20 }}>
                <div className="col-md-9" style={{ wordBreak: "break-word", maxWidth: "100%" }}>
                  <span style={{ width: "4rem" }}>You:</span>
                  <span style={{ maxWidth: "80ch", marginLeft: 10, whiteSpace: "pre-wrap" }}>{msg.reply}</span>
                </div>
                <div className="col-md-3">
                  <span className="datetime" style={{ fontSize: 14, color: "#898989", marginLeft: "20%" }}>
                    Created at: {msg.created_at}
                  </span>
                </div>
              </div>
            )
          )}
          {msg.admin_reply !== null && (
            <span className="spanWithMargin" style={{ color: "#035189", fontWeight: 600 }}>
              Support: <span>{msg.admin_reply}</span>
            </span>
          )}
        </p>
        {msg.image && (
          <img src={msg.image} style={{ maxWidth: isMobile ? 150 : "40rem", maxHeight: isMobile ? 150 : "30rem", border: "2px solid #f47c20", borderRadius: 8 }} />
        )}
      </div>
    ))}

    {canReply && (
      <div style={{ display: "flex", marginTop: 15 }}>
        <input ref={fileInputRef} type="file" accept="image/*" onChange={onAttach} style={{ display: "none", position: "absolute", zIndex: 1, height: 40, width: 40 }} />
        {selectedImageRply && <div style={{ position: "absolute", zIndex: 1, width: 10, height: 10, backgroundColor: "red", borderRadius: "50%" }} />}
        <Button style={{ width: 30, height: 30, marginLeft: 0, backgroundColor: "#f47c2000", border: "none", color: "white" }} onClick={() => fileInputRef.current?.click()}>
          <img src="./filepin.png" style={{ opacity: 1 }} />
        </Button>
        <textarea placeholder="Content"
          style={{ minHeight: 100, maxHeight: 300, padding: 10, width: isMobile ? "50%" : "100%", backgroundColor: "transparent", color: "black", borderRadius: 30, border: "2px solid grey" }}
          value={replyText} onChange={(e) => setReplyText(e.target.value)} />
        <Button style={{ height: 40, marginLeft: 10, marginTop: 30, backgroundColor: "#f47c20", border: "none", color: "white" }} onClick={onReply}>
          Reply
        </Button>
      </div>
    )}
  </div>
));

// ─── Single ticket card — replaces 4+ copy-pasted ticket card JSX blocks ─────
const TicketCard = memo(({ item, idx, isMobile, showMore, showMoreID, setShowMore, setShowMoreID, replies, canReply, fileInputRef, selectedImageRply, onAttach, replyText, setReplyText, onReply, onStatusChange, onViewImage }) => {
  const isExpanded = showMore && showMoreID === idx;
  const fontSize   = isMobile ? 12 : 24;

  return (
    <div className="table-row-noti" key={idx}>
      <div className="bar" style={isMobile ? { margin: "0px 5px" } : undefined} />
      <div className="message" style={isMobile ? { margin: "5px 0" } : undefined}>
        <Button style={{ marginLeft: 0, marginTop: 5, background: "#035189", border: "none", fontSize: isMobile ? 10 : 20, padding: "5px 15px", color: "#fff", fontWeight: 600 }}>
          Ticket No.: {item.enquiry_unique_id}
        </Button>

        {item.vm_id !== "null" && item.vm_id !== null && (
          <div className="desc">Machine: {convertToString(item.vm_id)}</div>
        )}

        <div className="desc" style={{ fontSize, marginTop: 10, color: "#035189", maxHeight: 100, fontWeight: 600, maxWidth: isMobile ? 150 : "100rem" }}>
          Subject: {item.type}
        </div>
        <div className="desc" style={{ fontSize, marginTop: 10, color: "#f47c20", maxHeight: 100, fontWeight: 600, maxWidth: isMobile ? undefined : "100rem", wordWrap: isMobile ? "break-word" : undefined, overflow: isMobile ? "hidden" : undefined }}>
          Desc.: {item.enquiry}
        </div>

        {item.image && (
          <div style={{ marginTop: 10 }}>
            <button onClick={() => onViewImage(item.image)} style={{ border: "none", background: "none", padding: 0 }}>
              <img src={item.image} style={{ maxWidth: isMobile ? 150 : "40rem", maxHeight: isMobile ? 150 : "30rem", border: "2px solid #f47c20", borderRadius: 8 }} />
            </button>
          </div>
        )}

        <div style={{ fontSize: 24 }} onClick={() => { setShowMore(!showMore); setShowMoreID(idx); }}>
          <Button style={{ marginLeft: 0, marginTop: 10, background: "#035189", border: "none", fontSize: isMobile ? 12 : 20, padding: "5px 15px", color: "#fff", fontWeight: 600, marginBottom: 10 }}>
            {isExpanded ? "Hide" : "Show More"}
          </Button>
        </div>

        {isExpanded && (
          <ReplyThread
            replies={replies}
            isMobile={isMobile}
            canReply={canReply}
            fileInputRef={fileInputRef}
            selectedImageRply={selectedImageRply}
            onAttach={onAttach}
            replyText={replyText}
            setReplyText={setReplyText}
            onReply={onReply}
          />
        )}
      </div>

      <div className="datetime" style={{ marginLeft: isMobile ? "60%" : "80%", marginRight: 0, position: "absolute", top: 15, fontSize: isMobile ? 10 : undefined }}>
        Created at: {item.createdAt}
        <StatusSelect item={item} onChange={onStatusChange} fontSize={isMobile ? 12 : 18} />
      </div>
    </div>
  );
});

// ─── Ticket tab buttons ───────────────────────────────────────────────────────
const TICKET_BUTTONS = ["open", "progress", "complete", "archived", ""];
const TICKET_LABELS  = ["Open", "inProgress", "Closed", "Archive", "All"];

const TicketTabs = memo(({ ticketType, setTicketType }) => (
  <div style={{ marginLeft: "auto" }}>
    {TICKET_BUTTONS.map((title, idx) => (
      <Button key={idx} style={{ background: (idx === 1 && ticketType === "inProgress") || ticketType === title ? "#f47c20" : "#035189", border: "none", fontSize: 14, padding: "5px 15px", color: "#fff", fontWeight: 600, borderRadius: 20, marginBottom: 10 }}
        onClick={() => setTicketType(title)}>
        {TICKET_LABELS[idx]}
      </Button>
    ))}
  </div>
));

// ─── Search bar ───────────────────────────────────────────────────────────────
const SearchBar = memo(({ value, onChange }) => (
  <div className="input-container" style={{ border: "2px solid #035189", width: "10rem", marginTop: 0, height: 45 }}>
    <input type="text" name="search" className="input-signup input-tickets" placeholder="Search" value={value}
      style={{ color: "black", textAlign: "center", width: 10 }} onChange={(e) => onChange(e.target.value)} />
    {value && (
      <button style={{ backgroundColor: "transparent", border: "none" }} onClick={() => onChange("")}>
        <FaX style={{ marginBottom: 2, color: "#154e7a", fontSize: 19 }} />
      </button>
    )}
  </div>
));

// ─── Main Component ───────────────────────────────────────────────────────────
const UserSupportPage = () => {
  const { smuser }    = useAuth();
  const location      = useLocation();
  const fileInputRef  = useRef(null);

  const selectedUser    = location.state?.userDetail ?? null;

  const [isMobile, setIsMobile]                     = useState(isMobileDevice);
  const [loading, setLoading]                       = useState(true);
  const [activeButton, setActiveButton]             = useState("");
  const [machineData, setMachineData]               = useState([]);
  const [supportMsg, setSupportMsg]                 = useState("");
  const [supportMachineArr, setSupportMachineArr]   = useState([]);
  const [searchText, setSearchText]                 = useState("");
  const [showMore, setShowMore]                     = useState(false);
  const [showMoreID, setShowMoreID]                 = useState(null);
  const [enquiries, setEnquiries]                   = useState(null);
  const [enqID, setEnqID]                           = useState("");
  const [replyArr, setReplyArr]                     = useState(null);
  const [replyText, setReplyText]                   = useState("");
  const [ticketType, setTicketType]                 = useState("");
  const [selectedImage, setSelectedImage]           = useState(null);
  const [selectedImageRply, setSelectedImageRply]   = useState(null);
  const [showImagePopup, setShowImagePopup]         = useState(false);
  const [showPreviewImagePopup, setPreviewImagePopup] = useState(null);

  // ── Toast helper ────────────────────────────────────────────────────────────
  const showToast = useCallback((msg) =>
    toast((t) => <AppToast id={t.id} message={msg} isMobile={isMobile} />),
    [isMobile]);

  // ── Mount — fix: original had [isMobile, enqID] as deps causing re-fetch
  // on every resize. Changed to [] — fetch once, resize handled by listener.
  useEffect(() => {
    window.scrollTo(0, 0);
    GetMachines();
    GetTickets();
    const handleResize = () => setIsMobile(isMobileDevice());
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // ── API helpers ─────────────────────────────────────────────────────────────
  const apiPost = useCallback(async (url, payload) => {
    const enc = await apiEncryptRequest(payload);
    const res = await instance.post(url, enc);
    return decryptData(res.data);
  }, []);

  // ── Fetch VMs ───────────────────────────────────────────────────────────────
  const GetMachines = useCallback(async () => {
    try {
      const data = await apiPost("/machines", { user_id: selectedUser.id });
      setMachineData(Object.values(data.vm));
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [selectedUser?.id, apiPost]);

  // ── Fetch tickets ───────────────────────────────────────────────────────────
  const GetTickets = useCallback(async () => {
    if (!selectedUser) { showToast("Oops! Something went wrong."); return; }
    setLoading(true);
    try {
      const data = await apiPost("/childsupportlist", { user_id: selectedUser.id });
      setReplyArr(data.enquiry_replys);
      setEnquiries(data.enquiry_list);
      const hasProgress = data.enquiry_list?.some((i) => i.status === "progress");
      setTicketType(hasProgress ? "inProgress" : "open");
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [selectedUser?.id, apiPost, showToast]);

  // ── Reply to ticket ─────────────────────────────────────────────────────────
  const TicketsReply = useCallback(async (replyID, ID) => {
    if (!replyText || !replyID) { showToast("Please write your reply!"); return; }
    setLoading(true);
    const form = new FormData();
    form.append("file",          selectedImageRply ?? "");
    form.append("id",            replyID);
    form.append("reply_message", replyText);
    form.append("enquiry_id",    ID);
    form.append("user_id",       smuser.id);
    try {
      const res = await instance.post("/reply_enquiry", form);
      if (res.status) { setReplyText(""); setSelectedImageRply(null); GetTickets(); }
    } catch { showToast("Oops! Something went wrong."); }
    setLoading(false);
  }, [replyText, selectedImageRply, smuser?.id, GetTickets, showToast]);

  // ── Archive / status change ──────────────────────────────────────────────────
  const ArchiveTicket = useCallback(async (data) => {
    setLoading(true);
    try {
      const res = await instance.post("/archived_enquiry", { enquiry_id: data.id, user_id: smuser.id, status: data.status });
      if (res.status) GetTickets();
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [smuser?.id, GetTickets]);

  // ── File size guard ──────────────────────────────────────────────────────────
  const handleImageChange = useCallback((e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > MAX_FILE_SIZE) { showToast("Selected file size exceeds 2MB. Please choose a smaller file."); e.target.value = null; return; }
    setSelectedImage(file);
  }, [showToast]);

  const handleImageUpdateRply = useCallback((e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > MAX_FILE_SIZE) { toast.error("File size exceeds 2MB. Please choose a smaller file."); e.target.value = null; return; }
    setSelectedImageRply(file);
  }, []);

  // ── Get replies for a ticket ─────────────────────────────────────────────────
  const getReplies = useCallback((enquiryId) =>
    replyArr ? [].concat(...replyArr).filter((i) => i.enquiry_id === enquiryId) : [],
    [replyArr]);

  // ── Filtered ticket lists ────────────────────────────────────────────────────
  const filteredTickets = useMemo(() => {
    if (!enquiries) return [];
    let list = enquiries;
    if (ticketType === "inProgress") list = list.filter((i) => i.status === "progress");
    else if (ticketType === "complete") list = list.filter((i) => i.status === "completed");
    else if (ticketType === "archived") list = list.filter((i) => i.archived === "archived");
    else if (ticketType) list = list.filter((i) => i.status === ticketType);
    return list.filter((i) => i.enquiry_unique_id.toLowerCase().includes(searchText.toLowerCase()));
  }, [enquiries, ticketType, searchText]);

  // ── Shared ticket card renderer ──────────────────────────────────────────────
  const renderTicket = useCallback((item, idx) => {
    const replies  = getReplies(item.id);
    const lastId   = replies.length > 0 ? replies[replies.length - 1].id : null;
    const canReply = item.status !== "completed" && item.status !== "archived";

    return (
      <TicketCard
        key={item.id ?? idx}
        item={item} idx={idx} isMobile={isMobile}
        showMore={showMore} showMoreID={showMoreID}
        setShowMore={setShowMore} setShowMoreID={setShowMoreID}
        replies={replies} canReply={canReply}
        fileInputRef={fileInputRef} selectedImageRply={selectedImageRply}
        onAttach={handleImageUpdateRply}
        replyText={replyText} setReplyText={setReplyText}
        onReply={() => lastId && TicketsReply(lastId, item.id)}
        onStatusChange={ArchiveTicket}
        onViewImage={(src) => { setPreviewImagePopup(src); setShowImagePopup(true); }}
      />
    );
  }, [isMobile, showMore, showMoreID, selectedImageRply, replyText, getReplies, handleImageUpdateRply, TicketsReply, ArchiveTicket]);

  // ── Controls bar (tabs + search) — shared mobile + web ───────────────────────
  const controls = (
    <div className="buttons-container" style={{ display: "flex", marginTop: 15, marginLeft: -5, marginBottom: -15 }}>
      <Button style={{ background: "#035189", border: "none", fontSize: 20, padding: "5px 15px", color: "#fff", fontWeight: 600, marginBottom: 10 }}>Latest</Button>
      <SearchBar value={searchText} onChange={setSearchText} />
      <TicketTabs ticketType={ticketType} setTicketType={setTicketType} />
    </div>
  );

  // ── Ticket list area ──────────────────────────────────────────────────────────
  const ticketList = (
    <div style={{ marginTop: 30 }}>
      {filteredTickets.map((item, idx) => renderTicket(item, idx))}
      {filteredTickets.length === 0 && (
        <div style={{ position: "relative", left: "40%", marginTop: 15, fontSize: 24, fontWeight: 400 }}>No Records</div>
      )}
    </div>
  );

  // ─── Render ───────────────────────────────────────────────────────────────────
  return (
    <div style={{ width: "100%", minHeight: "65rem", position: "relative", backgroundImage: "url(/main-bg.jpg)", backgroundSize: "cover", backgroundRepeat: "round", backgroundBlendMode: "overlay" }}>

      {/* Image preview popup */}
      {showImagePopup && (
        <div style={{ display: "grid", justifyItems: "center" }}>
          <div style={{ backdropFilter: "blur(5px)", borderRadius: 12, border: "2px solid #e97730", top: "30%", position: "absolute", zIndex: 999999999999, width: isMobile ? "80%" : "30%", height: isMobile ? "30%" : "30rem" }}>
            <div style={{ display: "grid", justifyItems: "center" }}>
              <button style={{ zIndex: 9, position: "absolute", backgroundColor: "white", border: "none", right: 0 }} onClick={() => setShowImagePopup(false)}>
                <FaX style={{ marginTop: 5, color: "#e97730", fontSize: 19 }} />
              </button>
              {showPreviewImagePopup && (
                <img src={showPreviewImagePopup} style={{ width: "70%", height: isMobile ? "80%" : "70%", borderRadius: 8, backgroundColor: "#f47c20", marginTop: 55 }} />
              )}
            </div>
          </div>
        </div>
      )}

      {isMobile ? (
        /* ── MOBILE ──────────────────────────────────────────────────────── */
        <div style={{ width: "100%", minHeight: "65rem", position: "relative", backgroundImage: "url(./main-bg.jpg)", backgroundSize: "cover", backgroundRepeat: "round", backgroundBlendMode: "overlay" }}>
          <div className="heading-dotted-support">Support <span /></div>
          <div className="features-section-solution" style={{ marginTop: 0 }}>
            <div className="heading-dotted-support" style={{ marginLeft: -4 }}>tickets </div>
            {controls}
            <div className="notification-cont" style={{ height: "50vh", marginBottom: "3rem" }}>
              {ticketList}
            </div>
          </div>
        </div>
      ) : (
        /* ── WEB ─────────────────────────────────────────────────────────── */
        <div className="features-page-solution" style={{ height: "100%", padding: "5rem" }}>
          <div className="features-section-solution">
            <Row>
              <div className="col-md-1" />
              <div className="col-md-10">
                {enquiries && (
                  <>
                    <div className="heading-dotted-support" style={{ marginLeft: -27 }}>tickets </div>
                    {enquiries.length > 0 && controls}
                    {ticketList}
                  </>
                )}
              </div>
              <div className="col-md-1" />
            </Row>
          </div>
        </div>
      )}

      <div className="apptoast-align">
        <Toaster position={isMobile ? "top-center" : "bottom-right"} reverseOrder={false} />
      </div>
      {loading && <div className="loading-overlay" style={{ zIndex: "9999999999999999" }}><Loader isLoading={loading} /></div>}
    </div>
  );
};

export default UserSupportPage;